// Sopwith 3D - fixed-point movement helpers.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Positions and velocities are 16.16 fixed point split across two fields:
// the integer part (x, dx) and the low 16 bits (lx, ldx). All arithmetic is
// done on 32-bit integers so results match the C original bit for bit.

import { ANGLES, SINTAB } from './constants';
import type { Obj } from './types';

export function COS(angle: number): number {
  return SINTAB[(angle + ANGLES / 4) % ANGLES];
}

export function SIN(angle: number): number {
  return SINTAB[angle % ANGLES];
}

export function imin(a: number, b: number): number {
  return a < b ? a : b;
}

export function imax(a: number, b: number): number {
  return a > b ? a : b;
}

export function clampRange(lo: number, v: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clampMax(v: number, hi: number): number {
  return v > hi ? hi : v;
}

export function inRange(lo: number, v: number, hi: number): boolean {
  return v >= lo && v <= hi;
}

/** C `abs()` for 32-bit ints. */
export function iabs(v: number): number {
  return v < 0 ? -v : v;
}

/**
 * Set an object's velocity from a x256-scaled vector (as produced by
 * `speed * COS(angle)`).
 */
export function setdxdy(ob: Obj, dx: number, dy: number): void {
  ob.dx = dx >> 8;
  ob.ldx = (dx << 8) & 0xffff;
  ob.dy = dy >> 8;
  ob.ldy = (dy << 8) & 0xffff;
}

/**
 * Advance an object one tick along its velocity. Returns the new integer
 * position. X is clamped to the world (using unsigned arithmetic, exactly as
 * the original did, so a negative X wraps to the far edge before clamping).
 * Y wraps at 16 bits; callers test `inRange(0, y, MAX_Y - 1)` afterward.
 */
export function movexy(ob: Obj, maxX: number, out: { x: number; y: number }): void {
  let pos = (((ob.x + ob.dx) << 16) + ob.lx + ob.ldx) >>> 0;
  const limit = ((maxX - 10) << 16) >>> 0;
  if (pos > limit) {
    pos = limit;
  }
  ob.x = (pos >>> 16) & 0xffff;
  ob.lx = pos & 0xffff;
  out.x = ob.x;

  pos = (((ob.y + ob.dy) << 16) + ob.ly + ob.ldy) >>> 0;
  ob.y = (pos >>> 16) & 0xffff;
  ob.ly = pos & 0xffff;
  out.y = ob.y;
}
